import dayjs, {Dayjs} from 'dayjs';
import {cloneDeep, isFunction} from 'lodash-es';

export const MappingKey = Symbol();

export type JsonMapper = {
    /**
     * To JSON
     */
    to?(value?: any, record?: any);
    /**
     * From JON
     */
    from?(value?: any, jsonData?: any, strict?: boolean);
};

export const JsonMappers = {
    Dayjs: {
        to: (value: Dayjs) => value.valueOf(),
        from: (value: string | number, jsonData?: any, strict?: boolean) => {
            if (!dayjs(value).isValid()) {
                if (strict) {
                    throw new Error(`Invalid date: ${value}`);
                } else {
                    console.warn(`Invalid date: ${value}`);
                }
            }
            return dayjs.utc(value);
        }
    },
    Number: {
        to: (value: number) => value,
        from: (value: any, jsonData?: any, strict?: boolean) => {
            if (isNaN(value)) {
                if (strict) {
                    throw new Error(`Invalid number: ${value}`);
                } else {
                    console.warn(`Invalid number: ${value}`);
                }
            }
            return Number(value);
        }
    },
    Boolean: {
        to: (value: number) => value,
        from: (value: any, jsonData?: any, strict?: boolean) => {
            if (value !== 'true' && value !== 'false' && value !== true && value !== false) {
                if (strict) {
                    throw new Error(`Invalid boolean: ${value}`);
                } else {
                    console.warn(`Invalid boolean: ${value}`);
                }
            }
            return value === true || value === 'true';
        }
    },
    ArrayOf: <T>(recordClass: () => {new (): T}) => ({
        to: (value: T[]): any[] => (value ? value.map((v: T) => toJson(v)) : []),
        from: (value: any[], jsonData?: any, strict?: boolean): T[] =>
            value ? value.map((v: any) => fromJson(recordClass(), v)) : []
    }),
    InstanceOf: <T>(recordClass: () => {new (): T}) => ({
        from: recordClass,
        to: (value: T): T => toJson(value)
    })
};

export type JsonOptions = {
    mapper?: JsonMapper;
    jsonName?: string;
    value?: any;
    skip?: boolean;
    nullable?: boolean;
    strict?: boolean;
    force?: boolean; // force apply mapping even if value is undefined or null
};

export function json(): Function;
export function json(mapper: JsonMapper): Function;
export function json(options: JsonOptions): Function;
export function json(...args: any[]): Function {
    let options: JsonOptions = {};
    if (args.length) {
        if (args[0].to || args[0].from) {
            options.mapper = args[0];
        } else {
            options = args[0];
        }
    }
    return function (target: any, fieldName: string) {
        if (!options.jsonName) {
            options.jsonName = fieldName;
        }
        if (!target.constructor.hasOwnProperty(MappingKey)) {
            target.constructor[MappingKey] = new Map();
        }
        target.constructor[MappingKey].set(fieldName, options);
    };
}

// get mappimng from class and its parents
export function getMappings<T>(recordClass: {new (): T}): Map<string, JsonOptions> {
    const mappings: Map<string, JsonOptions> = new Map();
    let currentClass = recordClass;
    while (currentClass) {
        const currentMappings: Map<string, JsonOptions> = currentClass[MappingKey];
        if (currentMappings) {
            currentMappings.forEach((value: JsonOptions, key: string) => {
                if (!mappings.has(key)) {
                    mappings.set(key, value);
                }
            });
        }
        currentClass = Object.getPrototypeOf(currentClass);
    }
    return mappings;
}

export function getJsonName(recordClass: any, fieldName: string): string {
    const mappings: Map<string, JsonOptions> = getMappings(recordClass);
    let name = fieldName;
    if (mappings.has(fieldName)) {
        name = mappings.get(fieldName).jsonName;
    }
    return name || fieldName;
}

export function getMappedName(recordClass: any, fieldName: string): string {
    const mappings: Map<string, JsonOptions> = getMappings(recordClass);
    if (mappings) {
        const founded = Array.from(mappings.entries()).find((value: [string, JsonOptions]) => {
            const [key, options] = value;
            return options.jsonName === fieldName;
        });
        if (founded) {
            return founded[0];
        }
    }
    return fieldName;
}

export function fromJson<T>(recordClass: {new (): T}, jsonData: any): T {
    const mappings: Map<string, JsonOptions> = getMappings(recordClass);
    const obj: T = new recordClass();
    if (mappings) {
        Array.from(mappings.entries()).forEach((value: [string, JsonOptions]) => {
            const [fieldName, options] = value;
            const jsonValue = jsonData[options.jsonName];
            if (jsonValue === undefined) {
                obj[fieldName] = isFunction(options.value) ? options.value() : cloneDeep(options.value);

                if (
                    obj[fieldName] === undefined &&
                    options.mapper &&
                    options.mapper.from &&
                    options.mapper.from.length == 2
                ) {
                    obj[fieldName] = options.mapper.from(jsonValue, jsonData, options.strict);
                }
            } else if (options.mapper && options.mapper.from) {
                if (options.mapper.from.length) {
                    obj[fieldName] = options.mapper.from(jsonValue, jsonData, options.strict);
                } else {
                    const dt = options.mapper.from();
                    if (dt.prototype && dt.prototype.constructor.name) {
                        if (dt.prototype.constructor[MappingKey]) {
                            obj[fieldName] = fromJson(dt.prototype.constructor, jsonValue);
                        } else {
                            obj[fieldName] = Reflect.construct(dt, [jsonValue]);
                        }
                    } else {
                        obj[fieldName] = dt(jsonValue, jsonData);
                    }
                }
            } else {
                obj[fieldName] = jsonValue;
            }
        });
    }
    return obj;
}

export function toJson<T>(record: T): any {
    const mappings: Map<string, JsonOptions> = getMappings(record.constructor as {new (): T});
    const jsonData: any = {};
    if (mappings) {
        Array.from(mappings.entries()).forEach((value: [string, JsonOptions]) => {
            const [fieldName, options] = value;
            const objValue = record[fieldName];
            if (objValue === null && options.nullable && !options.force) {
                jsonData[options.jsonName] = null;
            } else if ((!options.skip && objValue != undefined) || options.force) {
                // if (objValue !== undefined && (objValue === null && options.nullable) && !options.skip) {
                if (options.mapper && options.mapper.to) {
                    if (options.mapper.to.length) {
                        jsonData[options.jsonName] = options.mapper.to(objValue, record);
                    } else {
                        const dt = options.mapper.to();
                        if (dt.prototype && dt.prototype.constructor.name) {
                            jsonData[options.jsonName] = Reflect.construct(dt, [objValue]);
                        } else {
                            jsonData[options.jsonName] = dt(objValue, record);
                        }
                    }
                } else {
                    if (objValue.constructor && objValue.constructor[MappingKey]) {
                        jsonData[options.jsonName] = toJson(objValue);
                    } else {
                        jsonData[options.jsonName] = objValue;
                    }
                }
            }
        });
    }
    return jsonData;
}

export function stringToUint(string) {
    const str = btoa(unescape(encodeURIComponent(string)));
    const uintArray = [];
    const len = str.length;
    let i = -1;
    while (++i < len) {
        uintArray[i] = str.charCodeAt(i);
    }
    return new Uint8Array(uintArray);
}

export function uintToString(uintArray: ArrayBuffer) {
    var encodedString = String.fromCharCode.apply(null, uintArray),
        decodedString = decodeURIComponent(escape(atob(encodedString)));
    return decodedString;
}

export function toTransferableObject<T>(record: T): ArrayBuffer {
    const json = toJson(record);
    return stringToUint(JSON.stringify(json));
}
